/**
 * ew-sticky-cta — the fixed bottom CTA bar's one behaviour: get out of the way.
 *
 * The GemPages original shipped a per-page inline <script> that bound BOTH a scroll and a
 * resize handler and toggled inline display on every frame the user moved. This replaces
 * the whole thing with a single IntersectionObserver watching one element: the section the
 * button points at. No scroll handler, no resize handler, no layout reads.
 *
 * Two conditions hide the bar, matching the behaviour the live page actually has:
 *   1. the target section is on screen — the bar would otherwise cover the thing it links to
 *   2. the target section has been scrolled past — the customer already got there
 * Condition 2 is opt-out via data-hide-after-pass="false".
 *
 * Everything visual is CSS: this only writes data-state="shown" | "hidden". With JS off,
 * or with no target set, the bar stays visible and the button still works as a plain link.
 */
(() => {
  if (customElements.get('ew-sticky-cta')) return;

  class EwStickyCta extends HTMLElement {
    connectedCallback() {
      this.setAttribute('data-state', 'shown');
      this.bind();
      this.bindProxy();

      // In the theme editor a section re-render replaces the TARGET's DOM node, which would
      // leave the observer watching a detached element. Re-resolve on section load.
      if (window.Shopify && window.Shopify.designMode && !this.onSectionLoad) {
        this.onSectionLoad = () => this.bind();
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.release();

      if (this.onSectionLoad) {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        this.onSectionLoad = null;
      }
    }

    release() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    /**
     * Optional: forward the bar's click to a control elsewhere on the page rather than
     * following the href. The product page needs this — its bar's job is to open the buy
     * box's lens popup, and that popup's open state lives in the block that owns it, so
     * cloning the trigger would mean cloning the mechanics. Clicking the real one instead
     * keeps a single source of truth.
     *
     * Absent data-click-target, or with the target missing, or with JS off, the anchor
     * stays a plain working link. Nothing is prevented until a target is actually found.
     */
    bindProxy() {
      const selector = (this.getAttribute('data-click-target') || '').trim();
      if (!selector || this.proxyBound) return;

      const button = this.querySelector('.ew-sticky-cta__button');
      if (!button) return;

      this.proxyBound = true;
      button.addEventListener('click', (event) => {
        let target = null;
        try {
          target = document.querySelector(selector);
        } catch {
          return; // an invalid selector must not break the link
        }
        if (!target || target === button) return;

        event.preventDefault();
        target.click();
      });
    }

    bind() {
      this.release();

      const targetId = (this.getAttribute('data-target-id') || '').trim();
      if (!targetId || !('IntersectionObserver' in window)) return;

      const target = document.getElementById(targetId);
      if (!target) return;

      const hideAfterPass = this.getAttribute('data-hide-after-pass') !== 'false';

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          // boundingClientRect is viewport-relative: bottom <= 0 means fully scrolled past.
          const passed = entry.boundingClientRect.bottom <= 0;
          const hide = entry.isIntersecting || (hideAfterPass && passed);

          this.setAttribute('data-state', hide ? 'hidden' : 'shown');
        },
        { threshold: 0 }
      );

      this.observer.observe(target);
    }
  }

  customElements.define('ew-sticky-cta', EwStickyCta);
})();
